import { MoreDotsIcon } from '@tableau/icons/MoreDotsIcon';
import { FlyoutListMenuWidget, MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { style } from 'typestyle';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { IGetSiteEncryptionKeyDataKey } from '~src/settings/security-settings/sections/credential-encryption/models/CredentialEncryptionModels';
import { IGetSiteEncryptionKeyResult } from '~src/settings/security-settings/sections/extract-encryption/models/ExtractEncryptionModels';

import { getCredentialEncryptionKeyRequest } from '../actions/GetCredentialEncryptionKeyRequestAction';
import { openKMSKeyDeactivatePolicyDialog } from '../dialogs/KMSKeyDeactivateDialog';
import { openKMSKeyRestorePolicyDialog } from '../dialogs/KMSKeyRestoreDialog';
import { openKMSKeyRestoreForbiddenDialog } from '../dialogs/KMSKeyRestoreForbiddenDialog';


export interface KMSEncryptionKeyActionMenuState {
    isOpen: boolean;
}

export interface KMSEncryptionKeyActionMenuOwnProps {
    kmsEncrpytionKey: IGetSiteEncryptionKeyDataKey;
}

export interface KMSEncryptionKeyActionMenuStateProps {
    encryptionKey: IGetSiteEncryptionKeyResult;
}

export interface KMSEncryptionKeyActionMenuDispatchProps {
    retrieveCredentialEncryptionKey: () => void;
}

export type KMSEncryptionKeyActionMenuProps = KMSEncryptionKeyActionMenuOwnProps & KMSEncryptionKeyActionMenuDispatchProps & KMSEncryptionKeyActionMenuStateProps;


const exploreActionsMenuId = 'actions-menu-explorer-id';

const menuButtonDropdowStyle = style(
    { $nest: { '&&::after': { display: 'none' }, '&:disabled' : {borderColor: 'transparent !important'} } }
);

export const menuButtonStyle: React.CSSProperties = {
    borderColor: 'transparent', padding: 0, minWidth: 30
};

export class KMSEncryptionKeyActionMenuComponent extends React.Component<KMSEncryptionKeyActionMenuProps, KMSEncryptionKeyActionMenuState> {
    private buttonElement: HTMLElement | null = null;

    public constructor(props: KMSEncryptionKeyActionMenuProps) {
        super(props);

        this.state = {
            isOpen: false,
        };
    }

    private setButtonRef = (ref: HTMLButtonElement | null): void => {
        this.buttonElement = ref;
    };

    private onSelect(label: string) {
        switch (label) {
            case Messages.credentialEncrpytionKMS_DeactivateKey_button():
                openKMSKeyDeactivatePolicyDialog(this.props.kmsEncrpytionKey.dataKeyId);
                break;
            case Messages.credentialEncrpytionKMS_RestoreKey_button():
                if (this.canRestoreDataKey()) {
                    openKMSKeyRestorePolicyDialog(this.props.kmsEncrpytionKey.dataKeyId);
                } else {
                    openKMSKeyRestoreForbiddenDialog();
                }
                break;
          }
          this.setState({isOpen: false});
    }

    public render(): JSX.Element {
        return <>
            <MenuButtonWidget
                aria-controls={exploreActionsMenuId}
                isOpen={this.state.isOpen}
                onClick={() => this.setState({ isOpen: !this.state.isOpen })}
                ref={this.setButtonRef}
                testId='kms-encryption-key-actions-menu'
                density='low'
                className={menuButtonDropdowStyle}
                style={menuButtonStyle}
            >
                <MoreDotsIcon size={14}/>
            </MenuButtonWidget>
            {this.state.isOpen && <FlyoutListMenuWidget
                role='menu'
                anchorEl={this.buttonElement}
                allowAutoPosition
                id={exploreActionsMenuId}
                menuItemInfos={this.getMenuInfoItems()}
                onSelect= {(value: string) => this.onSelect(value)}
                requestCloseAll={() => this.setState({ isOpen: false })}
                testId='kms-encryption-key-actions-menu-list'
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                toggleEl={this.buttonElement!}
            />}
        </>;
    }

    private getMenuInfoItems(): MenuItemInfo[] {
        return [
            this.getActionButton(this.props.kmsEncrpytionKey.status)
        ];
    }

    private getActionButton(keyStatus: string): MenuItemInfo {
        switch(keyStatus) {
            case 'ACTIVE':
                return { displayValue: `${Messages.credentialEncrpytionKMS_DeactivateKey_button()}...`, value: Messages.credentialEncrpytionKMS_DeactivateKey_button(), disabled: true };
            case 'ARCHIVED':
                return { displayValue: `${Messages.credentialEncrpytionKMS_DeactivateKey_button()}...`, value: Messages.credentialEncrpytionKMS_DeactivateKey_button(), disabled: false };
            case 'INACTIVE':
                return { displayValue: `${Messages.credentialEncrpytionKMS_RestoreKey_button()}...`, value: Messages.credentialEncrpytionKMS_RestoreKey_button(), disabled: false };
            default:
                return { displayValue: '', value: '' };
        }
    }

    private canRestoreDataKey(): boolean {
        if (!this.props.encryptionKey || !this.props.encryptionKey.rootKeys) {
            return true;
        }

        for (const rootKey of this.props.encryptionKey.rootKeys) {
            if (this.props.kmsEncrpytionKey.rootKeyId === rootKey.externalKeyId && rootKey.keyStatus === 'DEACTIVATED') {
                return false;
            }
        }

        return true;
    }
}

const mapDispatchToProps: MapDispatchToProps<KMSEncryptionKeyActionMenuDispatchProps> =
  (dispatch) => bindActionCreators({
    retrieveCredentialEncryptionKey: getCredentialEncryptionKeyRequest
  },
  dispatch);

const mapStateToProps: MapStateToProps<KMSEncryptionKeyActionMenuStateProps> = (state: AppState) => ({
    encryptionKey: state.extractEncryptionInfo.encryptionKey
});

export const KMSEncryptionKeyActionMenu: React.ComponentClass<KMSEncryptionKeyActionMenuOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(KMSEncryptionKeyActionMenuComponent);